import { ErrorMessage, Formik } from "formik";
import { FC, useContext, useState } from "react";
import * as yup from "yup";

import appStyles from "../../../App.module.css";
import appConstants, { convertDateToApiFormat } from "../../../app-constants";
import { BusinessTravelContext } from "../../BusinessTravel";
import { businessTravelDocs } from "../../config/bt-constants";
import { IBusinessTravelContext, IOrderFormValues, IinitialQuote } from "../../config/interfaces";
import Popup from "../Popup";
import QuoteApi from "../api/QuoteApi";
import Footer from "../common/Footer";
import QuoteSummary from "../common/QuoteSummary";
import YourQuote from "../common/YourQuote";
import LoadingModal from "../modal/LoadingModal";

interface OrderFormProps {}

const OrderForm: FC<OrderFormProps> = (props: OrderFormProps) => {
  const { quoteData, setQuoteData, next, setOrder, setLoader, loader, tripsData } = useContext(
    BusinessTravelContext
  ) as IBusinessTravelContext;

  const [hasError, setHasError] = useState<string | null>(null);

  const initialFormValues: IOrderFormValues = {
    firstName: "",
    lastName: "",
    jobTitle: "",
    email: "",
    phone: "",
    companyName: "",
    companyAddress: "",
    postcode: "",
    city: "",
    host_country: quoteData?.host_country?.code || "",
    industry: "",
  };

  const validationSchema = yup.object().shape({
    firstName: yup.string().trim().required(),
    lastName: yup.string().trim().required(),
    jobTitle: yup.string().trim().required(),
    email: yup
      .string()
      .email("Invalid email address")
      .trim()
      .matches(appConstants.EMAIL_REGEX, "Invalid email address")
      .required(),
    phone: yup.string().trim().required(),
    companyName: yup.string().trim().required(),
    companyAddress: yup.string().trim().required(),
    postcode: yup.string().trim().required(),
    city: yup.string().trim().required(),
    host_country: yup.string().trim().required(),
    industry: yup.string().trim().required(),
  });
  const handleFormSubmit = async (values: IOrderFormValues) => {
    try {
      setLoader("order");
      const _quoteData: IinitialQuote = { ...quoteData };

      const _applicant = {
        first_name: values.firstName,
        last_name: values.lastName,
        email: values.email,
        contact_number: values.phone,
        address: values.companyAddress,
        post_code: values.postcode,
        city: values.city,
        host_country: values.host_country,
        country_code: quoteData?.host_country?.code,
      };

      const _applicants = [{ ...quoteData.applicants[0], ..._applicant }];

      _quoteData.applicants = _applicants;
      setQuoteData(_quoteData);
      const _depositDate = new Date();
      _depositDate.setDate(_depositDate.getDate() - 1);

      const _createOrderApiData = {
        currency_id: quoteData?.currency,
        product_id: quoteData?.productId,
        start_at: convertDateToApiFormat(quoteData.start_date),
        end_at: convertDateToApiFormat(quoteData.end_date),
        deposit_at: convertDateToApiFormat(_depositDate),
        details: [
          {
            type: "package",
            product_package_id: "base_package",
          },
          {
            type: "range",
            detail: "trip_range",
            value: String(quoteData?.noOfTrips?.till),
          },
        ],
        applicants: _applicants,
        meta: {
          company_name: values.companyName,
          job_title: values.jobTitle,
          industry: values.industry,
          company_size: quoteData.noOfEmployee,
        },
        destinations: [
          {
            destination_group: tripsData?.destinationCountries?.name,
          },
        ],
        affiliate: {
          id: quoteData?.affiliateId,
        },
      };
      const createOrderResponse = await QuoteApi.createOrder(_createOrderApiData);

      if (createOrderResponse.errors) {
        throw createOrderResponse;
      }

      setOrder(createOrderResponse.data);
      next();
    } catch (e: any) {
      setHasError(e.message);
      console.log(e);
    } finally {
      setLoader(null);
    }
  };

  return (
    <>
      {loader && (
        <Popup onClose={() => {}} title="" children={<LoadingModal />} closeButton={false} LoadingModal={true} />
      )}
      <div className={`${appStyles.mainContainer} ${appStyles.btMainContainer}`}>
        <div className={`${appStyles.quoteResultWrapper} ${appStyles.btQuoteResultWrapper}`}>
          <div className={`${appStyles.quoteDetailsBox}`}>
            <YourQuote />

            <Formik initialValues={initialFormValues} validationSchema={validationSchema} onSubmit={handleFormSubmit}>
              {({
                values,
                errors,
                touched,
                handleBlur,
                isValid,
                dirty,
                handleSubmit,
                handleChange,

                validateForm,
              }) => (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    await validateForm();
                    handleSubmit();
                  }}
                >
                  <div className={appStyles.btDestinationsInfoWrap}>
                    <p className={appStyles.btDestinationsInfoHeading}>Maximum Trip duration 31 days</p>
                    <b>
                      This policy covers trips on a worldwide basis except for travel to the following destinations:
                    </b>
                    &nbsp;{businessTravelDocs.excludedDestinations}
                  </div>
                  <div className={appStyles.formRow}>
                    <div className={appStyles.secondaryText}>
                      <span className={appStyles.infoIcon}></span>
                      <p>{businessTravelDocs?.warningMessage}</p>
                    </div>
                  </div>

                  <div
                    className={`${appStyles.quoteFormBlock} ${appStyles.quoteFormPersonalInformation} ${appStyles.businessBorderBox}`}
                  >
                    <div className={`${appStyles.quoteFormBlockTitle} ${appStyles.policyInfoWrap}`}>
                      <h3 className={`${appStyles.btTitle}`}>Your info</h3>
                      <p className={appStyles.btSubTitle}>
                        Please provide details of the primary contact for this insurance policy
                      </p>
                    </div>

                    <div className={`${appStyles.formRow} ${appStyles.btOrderFormRow}`}>
                      <div className={appStyles.formBlock}>
                        <div
                          className={`${appStyles.formField} ${
                            touched?.firstName && errors?.firstName ? appStyles.hasError : ""
                          }`}
                        >
                          <label className={appStyles.btFormLabel}>First name</label>
                          <input
                            type={"text"}
                            name={"firstName"}
                            id={"firstName"}
                            value={values.firstName}
                            className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput} ${appStyles.btPlaceholder}`}
                            placeholder={"First name"}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <ErrorMessage name={"firstName"}>
                            {(msg) => <div className={appStyles.formError}>This field is required</div>}
                          </ErrorMessage>
                        </div>
                      </div>

                      <div className={appStyles.formBlock}>
                        <div
                          className={`${appStyles.formField} ${
                            touched?.lastName && errors?.lastName ? appStyles.hasError : ""
                          }`}
                        >
                          <label className={appStyles.btFormLabel}>Last name</label>
                          <input
                            type={"text"}
                            name={"lastName"}
                            id={"lastName"}
                            value={values.lastName}
                            className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput} ${appStyles.btPlaceholder}`}
                            placeholder={"Last name"}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <ErrorMessage name={"lastName"}>
                            {(msg) => <div className={appStyles.formError}>This field is required</div>}
                          </ErrorMessage>
                        </div>
                      </div>
                    </div>

                    <div className={appStyles.formRow}>
                      <div className={appStyles.formBlock}>
                        <div className={appStyles.formRow}>
                          <label className={appStyles.btFormLabel}>Job title</label>
                        </div>
                        <div
                          className={`${appStyles.formField} ${
                            touched?.jobTitle && errors?.jobTitle ? appStyles.hasError : ""
                          }`}
                        >
                          <input
                            type={"text"}
                            name={"jobTitle"}
                            id={"jobTitle"}
                            value={values.jobTitle}
                            className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput} ${appStyles.btPlaceholder}`}
                            placeholder={"Enter your job title"}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <ErrorMessage name={"jobTitle"}>
                            {(msg) => <div className={appStyles.formError}>This field is required</div>}
                          </ErrorMessage>
                        </div>
                      </div>
                    </div>

                    <div className={`${appStyles.formRow} ${appStyles.btOrderFormRow}`}>
                      <div className={appStyles.formBlock}>
                        <div
                          className={`${appStyles.formField} ${
                            touched?.email && errors?.email ? appStyles.hasError : ""
                          }`}
                        >
                          <label className={appStyles.btFormLabel}>Email</label>
                          <input
                            type={"text"}
                            name={"email"}
                            id={"email"}
                            value={values?.email}
                            className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput} ${appStyles.btPlaceholder}`}
                            placeholder={"Email address"}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <ErrorMessage name={"email"}>
                            {(msg) => <div className={appStyles.formError}>This field is required</div>}
                          </ErrorMessage>
                        </div>
                      </div>

                      <div className={appStyles.formBlock}>
                        <div
                          className={`${appStyles.formField} ${
                            touched?.phone && errors?.phone ? appStyles.hasError : ""
                          }`}
                        >
                          <label className={appStyles.btFormLabel}>Phone</label>
                          <input
                            type={"text"}
                            name={"phone"}
                            id={"phone"}
                            value={values?.phone}
                            className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput} ${appStyles.btPlaceholder}`}
                            placeholder={"Phone number"}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <ErrorMessage name={"phone"}>
                            {(msg) => <div className={appStyles.formError}>This field is required</div>}
                          </ErrorMessage>
                        </div>
                      </div>
                    </div>

                    {/* Company Info  */}
                    <div>
                      <div className={appStyles.quoteFormBlockTitle}>
                        <h3 className={appStyles.btTitle}>Company info</h3>
                      </div>

                      <div className={appStyles.formRow}>
                        <label className={appStyles.btFormLabel}>Company name</label>
                      </div>
                      <div className={appStyles.formRow}>
                        <div className={appStyles.formBlock}>
                          <div
                            className={`${appStyles.formField} ${
                              touched?.companyName && errors?.companyName ? appStyles.hasError : ""
                            }`}
                          >
                            <input
                              type={"text"}
                              name={"companyName"}
                              id={"companyName"}
                              value={values.companyName}
                              className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput} ${appStyles.btPlaceholder}`}
                              placeholder={"Company name"}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <ErrorMessage name={`companyName`}>
                              {(msg) => <div className={appStyles.formError}>This field is required</div>}
                            </ErrorMessage>
                          </div>
                        </div>
                      </div>

                      <div className={appStyles.formRow}>
                        <div className={appStyles.formBlock}>
                          <div
                            className={`${appStyles.formField} ${
                              touched?.industry && errors?.industry ? appStyles.hasError : ""
                            }`}
                          >
                            <label className={appStyles.btFormLabel}>Industry</label>
                            <input
                              type={"text"}
                              name={"industry"}
                              id={"industry"}
                              value={values?.industry}
                              className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput} ${appStyles.btPlaceholder}`}
                              placeholder={"Enter the industry you work in"}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <ErrorMessage name={"industry"}>
                              {(msg) => <div className={appStyles.formError}>This field is required</div>}
                            </ErrorMessage>
                          </div>
                        </div>
                      </div>

                      <div className={appStyles.formRow}>
                        <div className={appStyles.formBlock}>
                          <div className={appStyles.formRow}>
                            <label className={appStyles.btFormLabel}>Company address</label>
                          </div>
                          <div
                            className={`${appStyles.formField} ${
                              touched?.companyAddress && errors?.companyAddress ? appStyles.hasError : ""
                            }`}
                          >
                            <input
                              type={"text"}
                              name={"companyAddress"}
                              id={"companyAddress"}
                              value={values.companyAddress}
                              className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput} ${appStyles.btPlaceholder}`}
                              placeholder={"Your business address"}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <ErrorMessage name={"companyAddress"}>
                              {(msg) => <div className={appStyles.formError}>This field is required</div>}
                            </ErrorMessage>
                          </div>
                        </div>
                      </div>

                      <div className={`${appStyles.formRow} ${appStyles.btOrderFormRow}`}>
                        <div className={appStyles.formBlock}>
                          <div
                            className={`${appStyles.formField} ${
                              touched?.city && errors?.city ? appStyles.hasError : ""
                            }`}
                          >
                            <label className={appStyles.btFormLabel}>City</label>
                            <input
                              type={"text"}
                              name={"city"}
                              id={"city"}
                              value={values.city}
                              className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput} ${appStyles.btPlaceholder}`}
                              placeholder={"City"}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <ErrorMessage name={"city"}>
                              {(msg) => <div className={appStyles.formError}>This field is required</div>}
                            </ErrorMessage>
                          </div>
                        </div>

                        <div className={appStyles.formBlock}>
                          <div
                            className={`${appStyles.formField} ${
                              touched?.postcode && errors?.postcode ? appStyles.hasError : ""
                            }`}
                          >
                            <label className={appStyles.btFormLabel}>Postal code</label>
                            <input
                              type={"text"}
                              name={"postcode"}
                              id={"postcode"}
                              value={values.postcode}
                              className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput} ${appStyles.btPlaceholder}`}
                              placeholder={"Postal code"}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <ErrorMessage name={"postcode"}>
                              {(msg) => <div className={appStyles.formError}>This field is required</div>}
                            </ErrorMessage>
                          </div>
                        </div>

                        <div className={appStyles.formBlock}>
                          <div
                            className={`${appStyles.formField} ${
                              touched?.host_country && errors?.host_country ? appStyles.hasError : ""
                            }`}
                          >
                            <label className={appStyles.btFormLabel}>Country</label>
                            <input
                              type={"text"}
                              name={"host_country"}
                              id={"host_country"}
                              value={quoteData?.host_country?.name}
                              className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput} ${appStyles.btPlaceholder}`}
                              disabled={true}
                            />
                            <ErrorMessage name={"host_country"}>
                              {(msg) => <div className={appStyles.formError}>This field is required</div>}
                            </ErrorMessage>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={appStyles.formRow}>
                    <div className={`${appStyles.buttonBlock} `}>
                      <button
                        className={`${appStyles.bfButton}  ${appStyles.btButton100}`}
                        type={"submit"}
                        disabled={!isValid || !dirty}
                      >
                        PROCEED TO CHECKOUT
                      </button>
                    </div>
                  </div>
                  <Footer />
                </form>
              )}
            </Formik>
            {hasError && (
              <div className={appStyles.formRow}>
                <div className={appStyles.battlefaceServerError}>{hasError}</div>
              </div>
            )}
          </div>
          <QuoteSummary />
        </div>
      </div>
    </>
  );
};

export default OrderForm;
